import { useEffect } from "react";

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "";
const COOKIE_PREFERENCES_KEY = "cookie_preferences";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const useGoogleAnalytics = () => {
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) {
      console.warn("Google Analytics: VITE_GA_MEASUREMENT_ID is not set");
      return;
    }

    const checkAndLoadGA = () => {
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      
      if (!savedPrefs) return;

      try {
        const preferences: CookiePreferences = JSON.parse(savedPrefs);
        
        if (preferences.analytics) {
          loadGoogleAnalytics();
        } else {
          disableGoogleAnalytics();
        }
      } catch {
        // Invalid preferences, don't load GA
      }
    };

    const loadGoogleAnalytics = () => {
      // Check if already loaded
      if (document.getElementById("ga-script")) return;

      // Enable tracking
      (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

      // Load gtag.js script
      const script = document.createElement("script");
      script.id = "ga-script";
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      // Initialize gtag
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }
      (window as any).gtag = gtag;
      
      gtag("js", new Date());
      gtag("config", GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        cookie_flags: "SameSite=None;Secure",
      });
    };

    const disableGoogleAnalytics = () => {
      (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    };

    // Initial check
    checkAndLoadGA();

    // Listen for storage changes (when user updates preferences)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === COOKIE_PREFERENCES_KEY) {
        checkAndLoadGA();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Custom event for same-tab updates
    const handlePreferencesUpdate = () => {
      checkAndLoadGA();
    };
    window.addEventListener("cookie-preferences-updated", handlePreferencesUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cookie-preferences-updated", handlePreferencesUpdate);
    };
  }, []);
};

// Utility function to track page views
export const trackPageView = (url: string) => {
  if (typeof (window as any).gtag === "function") {
    (window as any).gtag("event", "page_view", {
      page_path: url,
    });
  }
};

// Utility function to track custom events
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof (window as any).gtag === "function") {
    (window as any).gtag("event", eventName, eventParams);
  }
};
